import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { HoneybookReviewListDto, HoneybookSignInStartDto, VenueHoneybookDto, VenueHoneybookStatusDto } from '@lib/dto/honeybook.dto'
import type { FinishHoneybookSignInInput, StartHoneybookSwitchInput, UpdateHoneybookConnectionInput } from '@lib/input/honeybook.input'
import { honeybookService } from '@/services/HoneyBookService'
import { eventKeys, honeybookKeys, pipelineKeys } from './queryKeys'

/** A venue's HoneyBook connection, and whether HoneyBook can be connected at all. */
export function useVenueHoneybook(venueId: string, enabled = true): UseQueryResult<VenueHoneybookDto> {
  return useQuery({
    queryKey: honeybookKeys.venue(venueId),
    queryFn: () => honeybookService.venue(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
    // A read under way is watched until it finishes.
    refetchInterval: (query) => (query.state.data?.connection?.reading === true ? 5_000 : false),
  })
}

/** Whether the venue's pipeline has HoneyBook in it, for whoever sees the pipeline. Watched while the first read runs. */
export function useHoneybookStatus(venueId: string, enabled: boolean): UseQueryResult<VenueHoneybookStatusDto> {
  return useQuery({
    queryKey: honeybookKeys.status(venueId),
    queryFn: () => honeybookService.status(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
    refetchInterval: (query) => (query.state.data?.state === 'first_read' ? 30_000 : false),
  })
}

/** Read the venue's HoneyBook now. */
export function useRefreshHoneybook(venueId: string): UseMutationResult<VenueHoneybookDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => honeybookService.refresh(venueId),
    onSuccess: (dto) => queryClient.setQueryData(honeybookKeys.venue(venueId), dto),
  })
}

/** Where to send the person to sign in to HoneyBook. */
export function useStartHoneybookSignIn(venueId: string): UseMutationResult<HoneybookSignInStartDto, Error, void> {
  return useMutation({ mutationFn: () => honeybookService.startSignIn(venueId) })
}

/** Keep what HoneyBook's sign-in sent back. */
export function useFinishHoneybookSignIn(): UseMutationResult<VenueHoneybookDto, Error, FinishHoneybookSignInInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FinishHoneybookSignInInput) => honeybookService.finishSignIn(input),
    onSuccess: (done) => {
      if (done.connection !== null) queryClient.setQueryData(honeybookKeys.venue(done.connection.venueId), done)
    },
  })
}

/** Save what the venue's HoneyBook stages and meetings mean. Every period's pipeline moves at once. */
export function useSaveHoneybookSettings(venueId: string): UseMutationResult<VenueHoneybookDto, Error, UpdateHoneybookConnectionInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateHoneybookConnectionInput) => honeybookService.saveSettings(venueId, input),
    onSuccess: (dto) => {
      queryClient.setQueryData(honeybookKeys.venue(venueId), dto)
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.venue(venueId) })
    },
  })
}

export function useDisconnectHoneybook(venueId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => honeybookService.disconnect(venueId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: honeybookKeys.venue(venueId) }),
  })
}

export function useForgetHoneybook(venueId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => honeybookService.forget(venueId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: honeybookKeys.venue(venueId) }),
  })
}

/** The venue's events waiting for a person to say which HoneyBook booking each is, as of HoneyBook's last read. */
export function useHoneybookReview(venueId: string, readAt: string | null): UseQueryResult<HoneybookReviewListDto> {
  return useQuery({
    queryKey: honeybookKeys.reviewAfter(venueId, readAt),
    queryFn: () => honeybookService.review(venueId),
    enabled: venueId !== '',
    retry: false,
  })
}

/**
 * Settle an event waiting for review: the booking it is, or none (null). A
 * handed-over event changes at once, and so do the lists that show it.
 */
export function useResolveHoneybookReview(venueId: string): UseMutationResult<void, Error, { itemId: string; projectId: string | null }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, projectId }: { itemId: string; projectId: string | null }) =>
      projectId === null ? honeybookService.rejectReview(venueId, itemId) : honeybookService.confirmReview(venueId, itemId, { projectId }),
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: honeybookKeys.review(venueId) })
      if (projectId === null) return
      void queryClient.invalidateQueries({ queryKey: honeybookKeys.venue(venueId) })
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.venue(venueId) })
    },
  })
}

/** Match the venue's own events to HoneyBook's bookings again, with a full read now. */
export function useMatchHoneybookAgain(venueId: string): UseMutationResult<VenueHoneybookDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => honeybookService.matchAgain(venueId),
    onSuccess: (dto) => queryClient.setQueryData(honeybookKeys.venue(venueId), dto),
  })
}

/** Set the day the venue switches from HoneyBook to FLOH Venues. */
export function useStartHoneybookSwitch(venueId: string): UseMutationResult<VenueHoneybookDto, Error, StartHoneybookSwitchInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: StartHoneybookSwitchInput) => honeybookService.startSwitch(venueId, input),
    onSuccess: (dto) => queryClient.setQueryData(honeybookKeys.venue(venueId), dto),
  })
}

/** Call off a switch that hasn't happened yet. */
export function useCancelHoneybookSwitch(venueId: string): UseMutationResult<VenueHoneybookDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => honeybookService.cancelSwitch(venueId),
    onSuccess: (dto) => queryClient.setQueryData(honeybookKeys.venue(venueId), dto),
  })
}
